import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { Save, Settings, Clock, Upload, Globe, RefreshCw, AlertCircle } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function SystemConfig() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    app_name: 'FK Partners Admin',
    default_language: 'pt-BR',
    session_timeout: '30',
    max_upload_size: '500',
    timezone: 'America/Sao_Paulo',
    date_format: 'DD/MM/YYYY',
    items_per_page: '25',
    enable_notifications: true,
    enable_analytics: true,
  });

  useQuery({
    queryKey: ['system-config'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getSystemConfig(token);
      } catch (error) {
        console.error('Failed to fetch system config:', error);
        return null;
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('accessToken') || '';
      return await adminService.updateSystemConfig(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-config'] });
      showToast('success', 'Configuration Updated', 'System configuration updated successfully');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const configSections = [
    {
      icon: Settings,
      title: 'General Settings',
      description: 'Basic application configuration',
      fields: [
        {
          label: 'Application Name',
          name: 'app_name',
          type: 'text',
          placeholder: 'FK Partners Admin',
          help: 'Name displayed in the browser and header',
        },
        {
          label: 'Default Language',
          name: 'default_language',
          type: 'select',
          options: [
            { value: 'pt-BR', label: 'Portuguese (Brazil)' },
            { value: 'en-US', label: 'English (US)' },
          ],
          help: 'Default language for new users',
        },
        {
          label: 'Items Per Page',
          name: 'items_per_page',
          type: 'select',
          options: [
            { value: '10', label: '10 items' },
            { value: '25', label: '25 items' },
            { value: '50', label: '50 items' },
            { value: '100', label: '100 items' },
          ],
          help: 'Default pagination size for lists',
        },
      ],
    },
    {
      icon: Clock,
      title: 'Session & Security',
      description: 'Timeout and security settings',
      fields: [
        {
          label: 'Session Timeout',
          name: 'session_timeout',
          type: 'number',
          placeholder: '30',
          suffix: 'minutes',
          help: 'Auto-logout after inactivity',
        },
      ],
    },
    {
      icon: Upload,
      title: 'Upload Settings',
      description: 'File upload configuration',
      fields: [
        {
          label: 'Max Upload Size',
          name: 'max_upload_size',
          type: 'number',
          placeholder: '500',
          suffix: 'MB',
          help: 'Maximum file size for uploads',
        },
      ],
    },
    {
      icon: Globe,
      title: 'Localization',
      description: 'Date, time, and region settings',
      fields: [
        {
          label: 'Timezone',
          name: 'timezone',
          type: 'select',
          options: [
            { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
            { value: 'America/New_York', label: 'New York (UTC-5)' },
            { value: 'Europe/London', label: 'London (UTC+0)' },
            { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
          ],
          help: 'System timezone for date/time display',
        },
        {
          label: 'Date Format',
          name: 'date_format',
          type: 'select',
          options: [
            { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2026)' },
            { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2026)' },
            { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-12-31)' },
          ],
          help: 'Date format throughout the system',
        },
      ],
    },
    {
      icon: RefreshCw,
      title: 'Features',
      description: 'Enable or disable system features',
      fields: [
        {
          label: 'Enable Notifications',
          name: 'enable_notifications',
          type: 'toggle',
          help: 'Show system notifications to users',
        },
        {
          label: 'Enable Analytics',
          name: 'enable_analytics',
          type: 'toggle',
          help: 'Track usage analytics',
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">System Configuration</h1>
        <p className="text-sm text-gray-600 mt-1">
          Configure general system settings and preferences
        </p>
      </div>

      {/* Info Banner */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 mb-1">Configuration Tips</p>
            <p className="text-blue-700">
              Changes to these settings affect all users. Session timeout requires users to log in
              again. Make sure to save your changes before leaving this page.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Configuration Sections */}
        {configSections.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.title} className="card">
              <div className="flex items-start space-x-3 mb-6">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{section.title}</h2>
                  <p className="text-sm text-gray-600">{section.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {section.fields.map((field: any) => (
                  <div key={field.name} className={field.type === 'toggle' ? 'col-span-2' : ''}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>

                    {field.type === 'text' && (
                      <input
                        type="text"
                        value={formData[field.name as keyof typeof formData] as string}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        className="input"
                        placeholder={field.placeholder}
                      />
                    )}

                    {field.type === 'number' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData[field.name as keyof typeof formData] as string}
                          onChange={(e) =>
                            setFormData({ ...formData, [field.name]: e.target.value })
                          }
                          className="input"
                          placeholder={field.placeholder}
                        />
                        {field.suffix && (
                          <span className="text-sm text-gray-500">{field.suffix}</span>
                        )}
                      </div>
                    )}

                    {field.type === 'select' && (
                      <select
                        value={formData[field.name as keyof typeof formData] as string}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        className="input"
                      >
                        {field.options?.map((opt: any) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {field.type === 'toggle' && (
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{field.label}</p>
                          <p className="text-xs text-gray-500 mt-1">{field.help}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              [field.name]: !(formData[field.name as keyof typeof formData] as boolean),
                            })
                          }
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            (formData[field.name as keyof typeof formData] as boolean)
                              ? 'bg-primary-600'
                              : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              (formData[field.name as keyof typeof formData] as boolean)
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    )}

                    {field.help && field.type !== 'toggle' && (
                      <p className="text-xs text-gray-500 mt-1">{field.help}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Save Button */}
        <div className="flex items-center justify-between pt-6 border-t">
          <p className="text-sm text-gray-600">
            Last updated: {new Date().toLocaleDateString()} at{' '}
            {new Date().toLocaleTimeString()}
          </p>
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
